<template>
	<div class="settings-container">
		<h1>{{ $t('appbar.settings-menu.ManageUsers.title') }}</h1>
		<b-table
			striped
			outlined
			table-class="roster"
			tfoot-class="roster-footer"
			:fields="rosterFields"
			:items="enterpriseUsers"
			:sort-compare="sortCompare"
		>
			<template #cell(icon)>
				<svg class="person-icon" viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg">
					<path fill="#666666" stroke="#666666" d="M500 0C776.1 0 1000 223.9 1000 500 1000 776.1 776.1 1000 500 1000 223.9 1000 0 776.1 0 500 0 223.9 223.9 0 500 0ZM500 464.3C326.1 464.3 304.6 542.4 302 646.2L301.6 671.6 301.6 705.6C353 755.2 422.9 785.7 500 785.7 573.2 785.7 640 758.2 690.6 712.9L698.4 705.6 698.4 672.1C697.9 555.7 688 464.3 500 464.3ZM500 214.3L493.7 214.5C437.5 217.7 392.9 264.4 392.9 321.4 392.9 380.6 440.8 428.6 500 428.6 559.2 428.6 607.1 380.6 607.1 321.4 607.1 264.4 562.5 217.7 506.3 214.5L500 214.3Z" />
				</svg>
			</template>
			<template #cell(person)="data">
				<div class="name">
					{{ data.item.firstName }} {{ data.item.lastName }} <span class="work-info">{{ data.item.title }}</span>
				</div>
				<span class="work-info">{{ data.item.email }}</span>
			</template>
			<template #cell(role-owner)="data">
				<b-form-checkbox
					:id="data.item.id+'-OWNER'"
					v-model="data.item.roleOwner"
					:disabled="checkRoleBoxDisabled(data)"
					@input="setRole(data.item)"
				/>
			</template>
			<template #cell(role-admin)="data">
				<b-form-checkbox
					:id="data.item.id+'-ADMIN'"
					v-model="data.item.roleAdmin"
					:disabled="checkRoleBoxDisabled(data)"
					@input="setRole(data.item)"
				/>
			</template>
			<template #cell(role-editor)="data">
				<b-form-checkbox
					:id="data.item.id+'-EDITOR'"
					v-model="data.item.roleEditor"
					:disabled="checkRoleBoxDisabled(data)"
					@input="setRole(data.item)"
				/>
			</template>
			<template #cell(role-viewer)="data">
				<b-form-checkbox
					:id="data.item.id+'-VIEWER'"
					v-model="data.item.roleViewer"
					disabled
				/>
			</template>
			<template #cell(remove)="data">
				<b-button
					variant="danger"
					:disabled="$store.state.user.currentEnterprise.enterprise_user.role !== 'OWNER' || $store.state.user.id === data.item.id"
					@click="showRemoveModal(data)"
				>
					{{ $t('appbar.settings-menu.ManageUsers.remove') }}
				</b-button>
			</template>
			<template #custom-foot>
				<b-tr>
					<b-td colspan="2">
						&nbsp;
					</b-td>
					<b-td colspan="3" class="seats-required">
						{{ $t('appbar.settings-menu.ManageUsers.seatCount', formatCountSeats ) }}
					</b-td>
					<b-td>
						&nbsp;
					</b-td>
					<b-td>
						<b-button v-b-toggle.invite-menu class="d-flex justify-content-end">
							{{ $t('appbar.settings-menu.ManageUsers.inviteUser') }}
						</b-button>
					</b-td>
				</b-tr>
			</template>
		</b-table>
		<b-sidebar
			id="invite-menu"
			v-model="displayInviteForm"
			:title="$t('appbar.settings-menu.ManageUsers.inviteUser')"
			right
		>
			<b-form class="p-3">
				<p>{{ $t('appbar.settings-menu.ManageUsers.inviteDescription') }}</p>
				<b-form-group :label="$t('appbar.settings-menu.ManageUsers.email')" label-for="invite-email">
					<b-form-input id="invite-email" v-model="inviteForm.email" type="email" />
				</b-form-group>
				<b-form-group :label="$t('appbar.settings-menu.ManageUsers.inviteFirstName')" label-for="invite-first-name">
					<b-form-input id="invite-first-name" v-model="inviteForm.firstName" type="text" />
				</b-form-group>
				<b-form-group :label="$t('appbar.settings-menu.ManageUsers.inviteLastName')" label-for="invite-last-name">
					<b-form-input id="invite-last-name" v-model="inviteForm.lastName" type="text" />
				</b-form-group>
			</b-form>
			<template #footer="{ hide }">
				<b-button variant="danger" @click="hide">
					{{ $t('appbar.settings-menu.ManageUsers.cancel') }}
				</b-button>
				<b-button variant="primary" @click="inviteUserToEnterprise">
					{{ $t('appbar.settings-menu.ManageUsers.invite') }}
				</b-button>
			</template>
		</b-sidebar>
	</div>
</template>

<script>
export default {
	name: 'ManageUsersSettings',
	data() {
		return {
			enterpriseUsers: [],
			enterpriseDetails: {},
			inviteForm: {
				email: null,
				firstName: null,
				lastName: null,
			},
			displayInviteForm: false,
			rosterFields: [
				{ key: 'icon', label: '' },
				{ key: 'person', label: this.$t('appbar.settings-menu.ManageUsers.name'), sortable: true },
				{ key: 'role-owner', label: this.$t('appbar.settings-menu.ManageUsers.roles.OWNER') },
				{ key: 'role-admin', label: this.$t('appbar.settings-menu.ManageUsers.roles.ADMIN') },
				{ key: 'role-editor', label: this.$t('appbar.settings-menu.ManageUsers.roles.EDITOR') },
				{ key: 'role-viewer', label: this.$t('appbar.settings-menu.ManageUsers.roles.VIEWER') },
				{ key: 'remove', label: '' },
			],
			lastUserChange: {},
			seatsActive: 0,
		};
	},
	computed: {
		formatCountSeats() {
			return { active: this.seatsActive, total: this.enterpriseDetails.seatsPurchased };
		},
	},
	watch: {
		lastUserChange: function roleChanged(newVal, oldVal) {
			// prevents repeat firing when checkbox display is updated
			if (!(oldVal.userId === newVal.userId && oldVal.role === newVal.role)) {
				if (newVal.role !== newVal.oldRole) {
					if (newVal.role === 'VIEWER' && newVal.oldRole !== 'VIEWER') this.seatsActive--;
					if (newVal.role !== 'VIEWER' && newVal.oldRole === 'VIEWER') this.seatsActive++;
					this.updateUserRole(newVal);
				}
			}
		},
	},
	created() {
		this.fetchEnterpriseUsers();
	},
	methods: {
		sortCompare(a, b, key, _sortDesc, _formatter, compareOptions, compareLocale = 'en') {
			if (key === 'person') return a.lastName.localeCompare(b.lastName, compareLocale, compareOptions);
			return null;
		},
		async fetchEnterpriseUsers() {
			this.enterpriseUsers = [];
			this.seatsActive = 0;
			const enterpriseId = this.$store.state.user.currentEnterprise.id;
			try {
				this.axios.get(`/enterprises/${enterpriseId}/users`).then((response) => {
					const selectedEnterprise = response.data;
					for (const user of response.data.users) {
						if (user.enterprise_user.role !== 'VIEWER') this.seatsActive++;
						user.roleViewer = true;
						this.setRoleCheckboxes(user);
						this.enterpriseUsers.push(user);
					}
					delete selectedEnterprise.users;
					this.enterpriseDetails = selectedEnterprise;
				});
			} catch (error) {
				this.$bvToast.toast(this.$t('appbar.settings-menu.ManageUsers.errorLoadingUsers'), {
					title: this.$t('appbar.labels.error'),
					variant: 'danger',
				});
			}
		},
		setRoleCheckboxes(user) {
			switch (user.enterprise_user.role) {
				case 'OWNER':
					user.roleEditor = true;
					user.roleAdmin = true;
					user.roleOwner = true;
					break;
				case 'ADMIN':
					user.roleEditor = true;
					user.roleAdmin = true;
					user.roleOwner = false;
					break;
				case 'EDITOR':
					user.roleEditor = true;
					user.roleAdmin = false;
					user.roleOwner = false;
					break;
				default:
					user.roleEditor = false;
					user.roleAdmin = false;
					user.roleOwner = false;
					break;
			}
		},
		setRole(user) {
			const originalRole = user.enterprise_user.role;
			if (user.roleOwner) {
				user.enterprise_user.role = 'OWNER';
			} else if (user.roleAdmin) {
				user.enterprise_user.role = 'ADMIN';
			} else if (user.roleEditor) {
				user.enterprise_user.role = 'EDITOR';
			} else {
				user.enterprise_user.role = 'VIEWER';
			}
			this.setRoleCheckboxes(user);
			this.lastUserChange = {
				userId: user.id,
				name: `${user.firstName} ${user.lastName}`,
				enterpriseId: user.enterprise_user.enterpriseId,
				role: user.enterprise_user.role,
				oldRole: originalRole,
			};
		},
		async updateUserRole(data) {
			try {
				const response = await this.axios.patch(`/enterprises/${data.enterpriseId}/users/${data.userId}`, {
					role: data.role,
				});
				if (response.status === 200) {
					this.$bvToast.toast(this.$t('appbar.settings-menu.ManageUsers.updateRole', data), {
						title: this.$t('appbar.labels.success'),
						variant: 'success',
					});
				} else {
					this.$bvToast.toast(this.$t('appbar.settings-menu.ManageUsers.failed'), {
						title: this.$t('appbar.labels.error'),
						variant: 'danger',
					});
				}
			} catch (error) {
				this.$bvToast.toast(this.$t('appbar.settings-menu.ManageUsers.failed'), {
					title: this.$t('appbar.labels.error'),
					variant: 'danger',
				});
			}
		},
		async showRemoveModal(data) {
			const confirmation = await this.$msgBoxConfirm({
				message: this.$t('appbar.settings-menu.ManageUsers.removePrompt', {
					name: `${data.item.firstName} ${data.item.lastName}`,
					enterprise: this.enterpriseDetails.name,
				}),
				okTitle: this.$t('appbar.settings-menu.ManageUsers.remove'),
				title: this.$t('appbar.settings-menu.ManageUsers.removeModalHeading'),
			});
			if (confirmation) {
				try {
					this.axios
						.delete(`/enterprises/${this.enterpriseDetails.id}/users/${data.item.id}`)
						.then((response) => {
							if (response.status === 204) {
								this.fetchEnterpriseUsers();
								this.$bvToast.toast(this.$t('appbar.settings-menu.ManageUsers.userRemoved'), {
									title: this.$t('appbar.labels.success'),
									variant: 'success',
								});
							} else {
								this.$bvToast.toast(this.$t('appbar.settings-menu.ManageUsers.failed'), {
									title: this.$t('appbar.labels.error'),
									variant: 'danger',
								});
							}
						});
				} catch (error) {
					this.$bvToast.toast(this.$t('appbar.settings-menu.ManageUsers.failed'), {
						title: this.$t('appbar.labels.error'),
						variant: 'danger',
					});
				}
			}
		},
		async inviteUserToEnterprise() {
			try {
				this.$root.$emit('bv::toggle::collapse', 'invite-menu');
				const enterpriseId = this.$store.state.user.currentEnterprise.id;
				const data = {};
				if (this.inviteForm.lastName || this.inviteForm.firstName) {
					data.firstName = this.inviteForm.firstName;
					data.lastName = this.inviteForm.lastName;
				}
				this.axios
					.post(`/enterprises/${enterpriseId}/invite/${this.inviteForm.email}`, data)
					.then((response) => {
						if (response.status === 201) {
							this.$bvToast.toast(this.$t('appbar.settings-menu.ManageUsers.inviteSent'));
						} else if (response.status === 200) {
							this.$bvToast.toast(this.$t('appbar.settings-menu.ManageUsers.inviteExisting'));
						}
						this.fetchEnterpriseUsers();
					})
					.catch((error) => {
						if (error.response.status === 409) {
							this.$bvToast.toast(this.$t('appbar.settings-menu.ManageUsers.invitePendingUser'), {
								variant: 'warning',
								title: this.$t('appbar.labels.warning'),
							});
						} else {
							this.$bvToast.toast(this.$t('appbar.settings-menu.ManageUsers.failed'), {
								title: this.$t('appbar.labels.error'),
								variant: 'danger',
							});
						}
					});
			} catch (error) {
				this.$bvToast.toast(this.$t('appbar.settings-menu.ManageUsers.failed'), {
					title: this.$t('appbar.labels.error'),
					variant: 'danger',
				});
			}
		},
		checkRoleBoxDisabled(data) {
			// is the current user an Owner?
			if (this.$store.state.user.currentEnterprise.enterprise_user.role !== 'OWNER') return true;
			// if the user is an Owner, restrict changing their own role
			if (data.field.key === 'role-owner' && this.$store.state.user.id === data.item.id) return true;
			// if the row is an Owner, prevent changing Admin
			if (data.field.key === 'role-admin' && data.item.roleOwner) return true;
			// if the row is an Admin, prevent changing Editor
			if (data.field.key === 'role-editor' && data.item.roleAdmin) return true;
			// check if occupied seats == available seats, disable unchecked boxes for those that are currently VIEWERs
			if (this.seatsActive >= this.enterpriseDetails.seatsPurchased) {
				if (!(data.item.roleOwner || data.item.roleAdmin || data.item.roleEditor)) return true;
			}
			return false;
		},
	},
};
</script>

<style scoped>
.settings-container {
	flex: 1;
	overflow-y: auto;
	padding: 1rem;
}

.settings-container::after {
	content: '';
	display: block;
	height: 1rem;
	width: 100%;
}

.person-icon {
	width: 2rem;
}

.work-info {
	color: #999999;
	font-size: 0.9rem;
}
</style>
<style>
.roster td {
	vertical-align: middle !important;
}

.roster-footer {
	text-align: center;
}

.seats-required {
	background-color: #596d75;
	text-align: center;
	color: #ffffff;
}
</style>